import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, firstValueFrom, from, of, iif, throwError } from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import {
  catchError,
  concatMap,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  timeout,
} from 'rxjs';
import {
  ApiRouteDefinition,
  AuthClientConfig,
  HttpInterceptorConfig,
  isHttpInterceptorRouteConfig,
} from '@auth0/auth0-angular';
import { AUTH_PROVIDER } from '@core/auth/auth-provider.token';
import { IAuthProvider } from '@core/auth/auth-provider.interface';
import { AppConfigService } from '@core/services/config/app-config.service';

/**
 * How long a BFF session refresh may run before it is abandoned.
 *
 * The refresh is multicast, so every 401 raised while one is in flight waits
 * on the same observable. A server that accepts the request and then stalls
 * without closing the connection would never settle it, pinning
 * `_bffRefreshInFlight` for the life of the tab and leaving every later 401
 * waiting on an observable that can no longer emit. Giving up releases the
 * cross-tab lock and clears the in-flight reference, so the next 401 starts a
 * fresh attempt instead of inheriting a dead one.
 *
 * This bounds one refresh attempt, not time spent queued for the cross-tab
 * lock. A tab queued behind one stalled attempt can wait nearly twice this
 * interval before its original 401 surfaces; each additional queued stalled
 * attempt can add another interval.
 */
export const BFF_REFRESH_TIMEOUT_MS = 15_000;

@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {
  private _bffRefreshInFlight?: Observable<void>;

  constructor(
    private configFactory: AuthClientConfig,
    @Inject(AUTH_PROVIDER) private authProvider: IAuthProvider,
    private appConfig: AppConfigService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // BFF mode: cookies handle auth; no Authorization header needed.
    // Just ensure withCredentials is set for same-origin API requests.
    if (this.appConfig.config.authProvider === 'bff') {
      if (this._isApiRequest(req)) {
        const clone = req.clone({ withCredentials: true });
        return next.handle(clone).pipe(
          catchError((error: unknown) => {
            if (
              !(error instanceof HttpErrorResponse) ||
              error.status !== 401 ||
              this._isBffAuthEndpoint(req)
            ) {
              return throwError(() => error);
            }

            return this._refreshBffSession(next).pipe(
              // Preserve the original 401 when refresh is refused; callers should
              // then follow the existing sign-in path without learning IdP details.
              catchError((refreshError: unknown) => {
                if (
                  refreshError instanceof HttpErrorResponse &&
                  refreshError.status === 401
                ) {
                  // The BFF session has ended. Try to restore it silently (guarded
                  // to once per tab per window). Restored in a hidden frame: retry
                  // the request. Otherwise the 401 surfaces (a top-level silent
                  // redirect may be under way).
                  return this._restoreBffSessionSilently().pipe(
                    switchMap((restored) =>
                      restored ? of(void 0) : throwError(() => error)
                    )
                  );
                }
                return throwError(() => error);
              }),
              switchMap(() => next.handle(clone))
            );
          })
        );
      }
      return next.handle(req);
    }

    // Auth0/OIDC mode: existing bearer token logic
    const config = this.configFactory.get();
    if (!config.httpInterceptor?.allowedList || this._isPublicEndpoint(req)) {
      return next.handle(req);
    }
    return this._findMatchingRoute(req, config.httpInterceptor).pipe(
      concatMap((route) =>
        iif(
          () => route !== null,
          of(route).pipe(
            concatMap<any, Observable<string>>((r) =>
              this.authProvider
                .getAccessTokenSilently(r?.tokenOptions ?? undefined)
                .pipe(
                  catchError((err) => {
                    if (this._allowAnonymous(route, err)) {
                      return of('');
                    }
                    return throwError(() => err);
                  })
                )
            ),
            switchMap((token: string) => {
              const clone = token
                ? req.clone({
                    headers: req.headers.set(
                      'Authorization',
                      `Bearer ${token}`
                    ),
                  })
                : req;
              return next.handle(clone);
            })
          ),
          next.handle(req)
        )
      )
    );
  }

  private _isPublicEndpoint(req: HttpRequest<any>): boolean {
    return (
      req.url.includes('email-lookup-p') ||
      req.url.includes('/api/libraries') ||
      req.url.includes('/api/faqs') ||
      req.url.includes('/api/protocols') ||
      req.url.includes('/api/application/info')
    );
  }

  private _isApiRequest(req: HttpRequest<any>): boolean {
    const apiOrigin = this.appConfig.config.apiOrigin;
    return req.url.startsWith(apiOrigin) || req.url.startsWith('/api/');
  }

  private _isBffAuthEndpoint(req: HttpRequest<any>): boolean {
    return req.url.includes('/api/auth/');
  }

  private _restoreBffSessionSilently(): Observable<boolean> {
    if (this.authProvider.restoreSessionSilently) {
      return from(this.authProvider.restoreSessionSilently());
    }
    this.authProvider.trySilentSignIn?.();
    return of(false);
  }

  private _refreshBffSession(next: HttpHandler): Observable<void> {
    if (this._bffRefreshInFlight) {
      return this._bffRefreshInFlight;
    }

    const refreshUrl = `${this.appConfig.config.apiOrigin}/api/auth/refresh`;
    const refreshRequest = new HttpRequest('POST', refreshUrl, null, {
      withCredentials: true,
    });
    const sendRefresh = (): Observable<void> =>
      next.handle(refreshRequest).pipe(
        filter(
          (event): event is HttpResponse<unknown> =>
            event instanceof HttpResponse
        ),
        take(1),
        map(() => void 0),
        // Inside sendRefresh, so the bound applies before the cross-tab lock
        // and before the multicast: a stalled attempt must release the lock it
        // holds as well as free the interceptor's in-flight slot.
        timeout(BFF_REFRESH_TIMEOUT_MS)
      );
    const refresh = this._withCrossTabRefreshLock(sendRefresh).pipe(
      finalize(() => {
        if (this._bffRefreshInFlight === refresh) {
          this._bffRefreshInFlight = undefined;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this._bffRefreshInFlight = refresh;
    return refresh;
  }

  private _withCrossTabRefreshLock(
    refresh: () => Observable<void>
  ): Observable<void> {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      return refresh();
    }

    return from(
      navigator.locks.request('syrf-bff-session-refresh', () =>
        firstValueFrom(refresh())
      )
    );
  }

  private _stripQueryFrom(uri: string): string {
    if (uri.indexOf('?') > -1) {
      uri = uri.substr(0, uri.indexOf('?'));
    }
    if (uri.indexOf('#') > -1) {
      uri = uri.substr(0, uri.indexOf('#'));
    }
    return uri;
  }

  private _canAttachToken(
    route: ApiRouteDefinition,
    request: HttpRequest<any>
  ): boolean {
    const testPrimitive = (value: string | undefined): boolean => {
      if (!value) {
        return false;
      }
      const requestPath = this._stripQueryFrom(request.url);
      if (value === requestPath) {
        return true;
      }
      return (
        value.indexOf('*') === value.length - 1 &&
        request.url.startsWith(value.substr(0, value.length - 1))
      );
    };

    if (isHttpInterceptorRouteConfig(route)) {
      if (route.httpMethod && route.httpMethod !== request.method) {
        return false;
      }
      if (!route.uri && !route.uriMatcher) {
        console.warn(
          'Either a uri or uriMatcher is required when configuring the HTTP interceptor.'
        );
      }
      return route.uriMatcher
        ? route.uriMatcher(request.url)
        : testPrimitive(route.uri);
    }

    return testPrimitive(route);
  }

  private _findMatchingRoute(
    request: HttpRequest<any>,
    config: HttpInterceptorConfig
  ): Observable<ApiRouteDefinition | null> {
    for (const route of config.allowedList) {
      if (this._canAttachToken(route, request)) {
        return of(route);
      }
    }
    return of(null);
  }

  private _allowAnonymous(route: ApiRouteDefinition | null, err: any): boolean {
    return (
      !!route &&
      isHttpInterceptorRouteConfig(route) &&
      !!route.allowAnonymous &&
      ['login_required', 'consent_required'].includes(err.error)
    );
  }
}
